/**
 * G-64 — how fast the head runs, and how hard it burns.
 *
 * PER PRINTER. G-65 moved the stored value onto the printer profile in
 * `printerRegistry.ts`, which is what this comment always claimed: heat is a
 * property of the physical machine and the roll loaded in it, and a counter now
 * has two machines that can want different numbers. This module keeps the
 * VOCABULARY — the choices, what they mean in bytes, and the default.
 *
 * THE TWO SETTINGS FIGHT EACH OTHER, which is why both are offered and neither
 * is guessed. On this protocol `0xBD` is speed and LOWER IS QUICKER; the head
 * then has less time on each dot, so a faster setting at the same energy prints
 * fainter. Only the paper can say where the line is between "quick" and "grey",
 * so the florist turns the dials and looks, rather than an agent picking a
 * number from a reference written for someone else's printer and paper.
 */

/** G-64's global tuning key. Read once to migrate a tablet; never written. */
const LEGACY_TUNING_KEY = "fw-printer-tuning";

export type PrintSpeed = "standard" | "faster" | "fastest";
export type PrintDarkness = "light" | "normal" | "dark";

export interface PrintTuning {
  speed: PrintSpeed;
  darkness: PrintDarkness;
}

/**
 * Reference values for `0xBD`. 8 is what the driver itself uses to feed blank
 * paper, so it is known-safe for MOVING; whether the head keeps up while
 * BURNING at that rate is exactly what a test print answers.
 */
const SPEED_BYTES: Record<PrintSpeed, number> = {
  standard: 32,
  faster: 20,
  fastest: 12,
};

/** `0xAF`, two bytes. The reference reports the factory default near 0x3000. */
const ENERGY_VALUES: Record<PrintDarkness, number> = {
  light: 0x2000,
  normal: 0x3000,
  dark: 0x4500,
};

/**
 * Owner's call, 2026-09-11, on the real X5 at the pilot shop: run it flat out.
 *
 * The two go TOGETHER rather than by coincidence — at the quickest head speed
 * each dot gets the least time under the element, so the extra energy is what
 * keeps it black instead of grey. Either one alone would have been the wrong
 * half: fastest on normal energy prints faint, dark on a slow head just takes
 * longer and risks smudging.
 *
 * A tablet whose dials have been touched keeps its own choice; this is only
 * what a counter starts from.
 */
export const DEFAULT_TUNING: PrintTuning = {
  speed: "fastest",
  darkness: "dark",
};

/**
 * The tuning G-64 stored globally, for migrating a tablet that predates roles.
 *
 * Validates per field rather than rejecting the whole object, so a half-written
 * entry still yields usable dials instead of silently resetting both.
 */
export function readLegacyTuning(): PrintTuning {
  try {
    const raw = localStorage.getItem(LEGACY_TUNING_KEY);
    if (!raw) return DEFAULT_TUNING;
    const parsed = JSON.parse(raw) as Partial<PrintTuning>;
    return {
      speed:
        parsed.speed && parsed.speed in SPEED_BYTES
          ? parsed.speed
          : DEFAULT_TUNING.speed,
      darkness:
        parsed.darkness && parsed.darkness in ENERGY_VALUES
          ? parsed.darkness
          : DEFAULT_TUNING.darkness,
    };
  } catch {
    // Unreadable storage means the defaults, never a crash at the counter.
    return DEFAULT_TUNING;
  }
}

/** Turns the two choices into the bytes `toCatPrinterJob` wants. */
export function tuningToJobOptions(tuning: PrintTuning): {
  speed: number;
  energy: number;
} {
  return {
    speed: SPEED_BYTES[tuning.speed],
    energy: ENERGY_VALUES[tuning.darkness],
  };
}
